import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgNode } from '@/types/analytics.types';
import { scoreStyle, getInitials } from '@/utils/analytics';

interface OrgTreeNodeProps {
  node: OrgNode;
  depth?: number;
}

const TYPE_M: Record<string, { label: string; bg: string; color: string }> = {
  OU:     { label: 'OU',      bg: '#FFF7ED', color: '#C2410C' },
  BU:     { label: 'BU',      bg: '#EFF6FF', color: '#1D4ED8' },
  Dept:   { label: 'Dept',    bg: '#FDF2F8', color: '#BE185D' },
  Person: { label: 'Persona', bg: '#F0FDF4', color: '#166534' },
};

function omColor(om: number): string {
  if (om >= 10) return '#ef4444';
  if (om >= 5)  return '#f59e0b';
  return '#10b981';
}

export function OrgTreeNode({ node, depth = 0 }: OrgTreeNodeProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isP = node.t === 'Person';
  const sz = isP ? 32 : 38;
  const s = scoreStyle(node.sc ?? 0);
  const tm = TYPE_M[node.t] ?? { label: node.t, bg: '#f3f4f6', color: '#374151' };
  const om = node.om ?? 0;

  return (
    <div style={{ marginLeft: depth * 20 }}>
      <div
        className={`ona2-row${hasChildren ? ' clickable' : ''}${expanded && hasChildren ? ' open' : ''}`}
        onClick={() => hasChildren && setExpanded((v) => !v)}
        role={hasChildren ? 'button' : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
      >
        {/* Avatar */}
        <div
          className="ona2-avatar"
          style={{
            width: sz,
            height: sz,
            background: s.bg,
            borderColor: s.ring,
            color: s.text,
            fontSize: sz * 0.32,
          }}
        >
          {getInitials(node.n)}
        </div>

        {/* Info */}
        <div className="ona2-info">
          <div className="ona2-name-row">
            <span
              className="ona2-name"
              style={{ fontSize: isP ? 13 : 14, fontWeight: isP ? 500 : 600 }}
            >
              {node.n}
            </span>
            <span className="ona2-type" style={{ background: tm.bg, color: tm.color }}>
              {tm.label}
            </span>
            <span
              className="ona2-chip"
              style={{ background: s.bg, color: s.text, borderColor: s.ring }}
            >
              {(node.sc ?? 0) > 0 ? (node.sc!).toFixed(1) : '—'}
            </span>
          </div>
          <div className="ona2-meta">
            <span>
              {(node.ev ?? 0) > 0
                ? `${node.ev!.toLocaleString()} ${t('analytics.ona.evalsLabel')}`
                : t('analytics.ona.noEvals')}
            </span>
            {(node.p ?? 0) > 0 && !isP && (
              <span>· {node.p} {t('analytics.ona.collaboratorsLabel')}</span>
            )}
            {isP && om > 0 && (
              <span style={{ color: omColor(om), fontWeight: 500 }}>
                {om}% {t('analytics.ona.omissionLabel')}
              </span>
            )}
          </div>
        </div>

        {/* Om bar for non-Person nodes */}
        {!isP && (node.ev ?? 0) > 0 && (
          <div className="ona2-ombar">
            <div className="ona2-ombar-track">
              <div
                className="ona2-ombar-fill"
                style={{ width: `${Math.min(om, 100)}%`, background: omColor(om) }}
              />
            </div>
            <span style={{ color: omColor(om) }}>{om}%</span>
          </div>
        )}

        {/* Chevron */}
        {hasChildren && (
          <span className={`ona2-chevron${expanded ? ' open' : ''}`}>›</span>
        )}
      </div>

      {expanded && hasChildren && (
        <div className="ona2-children">
          {node.children!.map((child) => (
            <OrgTreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
